import { Type } from '@angular/core';
import { Alert, AlertEntity, PriorityConfig } from '../../models/alert';

export interface AlertPrimaryAction {
  label: string;
  icon: string;
  execute(): void;
}

/**
 * Declaration merging (interface + class with the same name) is intentional here.
 *
 * The interface re-exports all Alert properties onto the class type so that
 * subclasses and call-sites can access them (e.g. `vm.status`, `vm.priority`)
 * without wrapping or re-declaring every field. The constructor copies those
 * properties at runtime via Object.assign, so every field declared on the Alert interface
 * will be present after construction. TypeScript cannot statically verify this,
 * which is why the eslint suppression below is necessary.
 */
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export interface AlertViewModel<TEntity extends AlertEntity> extends Alert<TEntity> {}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class AlertViewModel<TEntity extends AlertEntity> {
  readonly priorityConfig: PriorityConfig | null;

  constructor(alert: Alert<TEntity>, priorityConfig: PriorityConfig | null = null) {
    Object.assign(this, alert);
    this.priorityConfig = priorityConfig;
  }

  /** Human-readable title for the alert row. */
  abstract get title(): string;

  /** Optional body copy under the title in the detail panel. */
  get description(): string | null {
    return null;
  }

  /** Replaces the Related section's rows when what they describe is gone, e.g. a deleted space. */
  get relatedNotice(): string | null {
    return null;
  }

  /**
   * Computed expiry date. Defaults to `expiresAt`; override per alert type
   * when expiry should come from the entity (e.g. event start/end).
   */
  get expiryDate(): Date | null {
    return this.expiresAt ? new Date(this.expiresAt) : null;
  }

  /**
   * Side-nav footer primary action. Return null to hide the footer.
   * Override per alert type to provide a label, icon, and action handler.
   */
  get primaryAction(): AlertPrimaryAction | null {
    return null;
  }

  /**
   * The component class to render in the Related section of the detail panel.
   * Returns null by default (no extra related rows).
   * Each entity-type VM overrides this to return its own self-sufficient component.
   */
  get relatedComponentType(): Type<unknown> | null {
    return null;
  }

  /**
   * Human-readable label for the alert type displayed in the Related section.
   * Returns the raw enum string by default; override per alert type for translations.
   */
  get alertTypeLabel(): string {
    return this.alertType;
  }
}
